/* RESPONSIBLE TEAM: team-proactive-support */
import Range from 'embercom/models/reporting/range';
import moment from 'moment-timezone';

/**
 * Interface for stats parameters object
 */
interface StatsParameters {
  contentId?: string;
  contentType?: string;
  range?: Range;
  [key: string]: any; // Allow additional properties
}

// Export the interface for use in other TypeScript files
export type { StatsParameters };

/**
 * Utility class for converting date ranges between timezones.
 * Used primarily for converting stats parameters from app timezone to browser timezone.
 */
export default class TimezoneConverter {
  /**
   * Converts stats parameters with date range from source timezone to target timezone
   *
   * @param statsParameters - The original stats parameters object
   * @param targetTimezone - The timezone to convert to (e.g., browser timezone)
   * @returns New stats parameters with converted range or null if no conversion needed
   */
  static convertStatsParameters(
    statsParameters: StatsParameters | null | undefined,
    targetTimezone: string,
  ): StatsParameters | null {
    if (!statsParameters) {
      return null;
    }

    // If no range exists, return the original statsParameters
    if (!statsParameters.range) {
      return statsParameters;
    }

    return this.convertStatsParametersRange(statsParameters, targetTimezone);
  }

  /**
   * Converts the date range within stats parameters to the target timezone
   *
   * @param statsParameters - The original stats parameters object with range
   * @param targetTimezone - The timezone to convert to
   * @returns New stats parameters with converted range
   */
  static convertStatsParametersRange(
    statsParameters: StatsParameters,
    targetTimezone: string,
  ): StatsParameters {
    let originalRange: Range = statsParameters.range!;

    // Skip conversion if source and target timezones are the same
    if (targetTimezone === originalRange.timezone) {
      return statsParameters;
    }

    let convertedRange: Range = this.convertRange(originalRange, targetTimezone);

    // Return a new statsParameters object with the converted range
    return {
      ...statsParameters,
      range: convertedRange,
    };
  }

  /**
   * Converts a Range object from its current timezone to the target timezone
   *
   * @param originalRange - The original range object
   * @param targetTimezone - The timezone to convert to
   * @returns New Range object with dates converted to target timezone
   */
  static convertRange(originalRange: Range, targetTimezone: string): Range {
    // Convert start date: take the date part from original timezone and interpret it in target timezone
    let startDateStr: string = moment
      .tz(originalRange.start, originalRange.timezone)
      .format('YYYY-MM-DD');
    let startInTargetTz: string = moment.tz(startDateStr, targetTimezone).format();

    // Convert end date: take the date part from original timezone and interpret it in target timezone
    let endDateStr: string = moment
      .tz(originalRange.end, originalRange.timezone)
      .format('YYYY-MM-DD');
    let endInTargetTz: string = moment.tz(endDateStr, targetTimezone).endOf('day').format();

    // Create a new range with the converted dates in target timezone
    return Range.createFromParams(startInTargetTz, endInTargetTz, targetTimezone);
  }

  /**
   * Gets the browser's timezone using Intl API
   *
   * @returns The browser's timezone identifier (e.g., 'America/New_York')
   */
  static getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}
